// useWorkflowStepItemAttachments.js

import { useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Services
import { subscribeToChildNodes } from 'services/nodes/NodeReadService';

// Utilities
import {
  filterWorkflowStepAttachmentNodes,
  filterWorkflowStepAttachmentsForItem,
} from 'node/missions/mission/utils/workflowStepAttachmentUtils';
import { normalizeTypeKeyList } from 'utilities/nodeTypeRelatedLists';

/**
 * useWorkflowStepItemAttachments
 *
 * Live TASK children stamped with a checklist item key.
 *
 * @param {object} params
 * @param {string} [params.itemKey]
 * @param {object|null} [params.stepNode]
 * @param {object|null} [params.user]
 * @returns {object[]} Attachments for the item.
 */
export default function useWorkflowStepItemAttachments({
  itemKey = '',
  stepNode = null,
  user = null,
} = {}) {
  const { getType } = useNodeTypes();
  const [relatedNodes, setRelatedNodes] = useState([]);

  const relatedTypeKeys = useMemo(() => {
    const hostType = getType(stepNode?.type);
    const configured = normalizeTypeKeyList(hostType?.suggestedChildren);
    return configured.length > 0 ? configured : null;
  }, [getType, stepNode?.type]);

  useEffect(() => {
    const nodeKey = String(stepNode?.key || '').trim();
    const baseKey = String(stepNode?.baseKey || '').trim();

    setRelatedNodes([]);

    if (!nodeKey || !user || !baseKey) {
      return undefined;
    }

    return subscribeToChildNodes(nodeKey, user, stepNode, (children) => {
      setRelatedNodes(Array.isArray(children) ? children : []);
    });
  }, [stepNode, user]);

  return useMemo(() => {
    const attachments = filterWorkflowStepAttachmentNodes(relatedNodes, relatedTypeKeys);
    return filterWorkflowStepAttachmentsForItem(attachments, itemKey);
  }, [itemKey, relatedNodes, relatedTypeKeys]);
}
